/**
 * SEO URL Mapping Utility
 * 
 * This file contains mapping functions to convert between old URL patterns and new SEO-friendly URLs
 * based on the facet_meta API response structure.
 * 
 * New URL Patterns:
 * - Listings: https://bulletscout.com/ls/{weapon_category}-ammo/{caliber}/{brand}?
 * - Products: https://bulletscout.com/pd/{weapon_category}-ammo/{caliber}/{brand}?
 * - Brands: https://bulletscout.com/brands/{brand}?
 * 
 * facet_meta API Response Structure:
 * {
 *   "status": 200,
 *   "short_message": "Success (cache)",
 *   "results": {
 *     "facets": {
 *       "bullet_type": [{"id": 1, "label": "Lead Hollow Point", "seo_slug": "lead-hollow-point"}],
 *       "bullet_type_s": [{"id": 1, "label": "Lead Solid", "seo_slug": "lead-solid"}],
 *       "bullet_weight": [{"id": 1, "label": "97 Gr", "seo_slug": "97-grain"}],
 *       "material": [{"id": 1, "label": "Brass", "seo_slug": "brass"}],
 *       "rdsperbox": [{"id": 1, "label": 2000, "seo_slug": "2000-rounds"}],
 *       "shell_length": [{"id": 2, "label": "2.75in", "seo_slug": "2-75-inch"}],
 *       "brand": [{"id": 1, "label": "1776 USA", "seo_slug": "1776-USA"}],
 *       "caliber": [{"id": 1, "label": "10mm Auto", "seo_slug": "10mm-auto"}],
 *       "weapon_category": [{"id": 1, "label": "handgun", "seo_slug": "handgun-ammo"}]
 *     }
 *   }
 * }
 */

// Mapping between old parameter names and facet types
export const PARAM_TO_FACET_MAPPING = {
  // Main URL parameters
  'wc': 'weapon_category',
  'caliber': 'caliber', 
  'brnd': 'brand',
  
  // Filter parameters
  'bt': 'bullet_type',
  'bts': 'bullet_type_s',
  'bw': 'bullet_weight',
  'mt': 'material',
  'rpb': 'rdsperbox',
  'sl': 'shell_length',
  
  // Additional parameters (keep as query params)
  'page': 'page',
  'sort_by': 'sort_by',
  'vendor_id': 'vendor_id',
  'rounds_per_box': 'rounds_per_box'
};

// Reverse mapping for SEO to old parameters
export const FACET_TO_PARAM_MAPPING = Object.fromEntries(
  Object.entries(PARAM_TO_FACET_MAPPING).map(([param, facet]) => [facet, param])
);

/**
 * Maps facet data to create lookup objects for ID to SEO slug conversion
 * @param {Object} facetData - The facets object from facet_meta API response
 * @returns {Object} - Object containing ID to SEO slug mappings and label to SEO slug mappings
 */
export const createFacetMappings = (facetData) => {
  const mappings = {};
  
  Object.keys(facetData).forEach(facetType => {
    // ID-based mappings (for old URL parameters)
    mappings[facetType] = {};
    
    // Label-based mappings (for product data)
    mappings[`${facetType}_label`] = {};
    
    // Reverse mappings (SEO slug to ID and SEO slug to label)
    mappings[`${facetType}_reverse`] = {};
    mappings[`${facetType}_label_reverse`] = {};
    
    facetData[facetType].forEach(item => {
      // ID to SEO slug mapping
      mappings[facetType][item.id] = item.seo_slug;
      
      // Label to SEO slug mapping (for product data)
      mappings[`${facetType}_label`][item.label] = item.seo_slug;
      
      // SEO slug to ID reverse mapping
      mappings[`${facetType}_reverse`][item.seo_slug] = item.id;
      
      // SEO slug to label reverse mapping
      mappings[`${facetType}_label_reverse`][item.seo_slug] = item.label;
    });
  });
  
  return mappings;
};

/**
 * Converts old URL parameters to new SEO URL structure
 * @param {Object} params - Current URL parameters
 * @param {Object} facetMappings - Facet mappings from createFacetMappings
 * @returns {Object} - New URL structure with SEO values
 */
export const convertToSeoUrl = (params, facetMappings) => {
  const seoParams = {};
  const queryParams = {};
  
  // Process each parameter
  Object.entries(params).forEach(([paramKey, paramValue]) => {
    if (!paramValue) return;
    
    const facetType = PARAM_TO_FACET_MAPPING[paramKey];
    
    if (facetType && facetMappings[facetType]) {
      // Convert to SEO slug
      if (paramKey === 'brnd' || paramKey === 'bt' || paramKey === 'bts' || 
          paramKey === 'bw' || paramKey === 'mt' || paramKey === 'rpb' || paramKey === 'sl') {
        // Multi-value parameters (comma-separated)
        const ids = paramValue.split(',').map(id => parseInt(id.trim())).filter(id => !isNaN(id));
        const slugs = ids.map(id => facetMappings[facetType][id]).filter(Boolean);
        
        if (slugs.length > 0) {
          // Map to appropriate SEO parameter name
          switch (paramKey) {
            case 'brnd':
              seoParams.brands = slugs;
              break;
            case 'bt':
              seoParams.bullet_types = slugs;
              break;
            case 'bts':
              seoParams.bullet_type_s = slugs;
              break;
            case 'bw':
              seoParams.bullet_weights = slugs;
              break;
            case 'mt':
              seoParams.materials = slugs;
              break;
            case 'rpb':
              seoParams.rounds_per_box = slugs;
              break;
            case 'sl':
              seoParams.shell_lengths = slugs;
              break;
          }
        }
      } else {
        // Single-value parameters
        const id = parseInt(paramValue);
        if (!isNaN(id) && facetMappings[facetType][id]) {
          seoParams[facetType] = facetMappings[facetType][id];
        }
      }
    } else {
      // Keep as query parameter
      queryParams[paramKey] = paramValue;
    }
  });
  
  return {
    seoParams,
    queryParams
  };
};

/**
 * Converts SEO URL back to old parameter format
 * @param {Object} seoParams - SEO URL parameters
 * @param {Object} facetMappings - Facet mappings from createFacetMappings
 * @returns {Object} - Old parameter format
 */
export const convertFromSeoUrl = (seoParams, facetMappings) => {
  const oldParams = {};
  
  // Process each SEO parameter
  Object.entries(seoParams).forEach(([seoKey, seoValue]) => {
    if (!seoValue) return;
    
    // Handle multi-value parameters (arrays)
    if (Array.isArray(seoValue)) {
      const facetType = getFacetTypeFromSeoKey(seoKey);
      const paramKey = FACET_TO_PARAM_MAPPING[facetType];
      
      if (facetType && paramKey && facetMappings[`${facetType}_reverse`]) {
        const ids = seoValue
          .map(slug => facetMappings[`${facetType}_reverse`][slug])
          .filter(Boolean);
        
        if (ids.length > 0) {
          oldParams[paramKey] = ids.join(',');
        }
      }
    } else {
      // Handle single-value parameters
      const facetType = seoKey;
      const paramKey = FACET_TO_PARAM_MAPPING[facetType];
      
      if (paramKey && facetMappings[`${facetType}_reverse`]) {
        const id = facetMappings[`${facetType}_reverse`][seoValue];
        if (id) {
          oldParams[paramKey] = id;
        }
      }
    }
  });
  
  return oldParams;
};

/**
 * Helper function to get facet type from SEO parameter key
 * @param {string} seoKey - SEO parameter key
 * @returns {string} - Facet type
 */
const getFacetTypeFromSeoKey = (seoKey) => {
  const mapping = {
    'brands': 'brand',
    'bullet_types': 'bullet_type',
    'bullet_type_s': 'bullet_type_s',
    'bullet_weights': 'bullet_weight',
    'materials': 'material',
    'rounds_per_box': 'rdsperbox',
    'shell_lengths': 'shell_length'
  };
  
  return mapping[seoKey] || seoKey;
};

/**
 * Builds new SEO URL from parameters
 * @param {string} type - URL type: 'ls' (listings), 'pd' (products), 'brands'
 * @param {Object} seoParams - SEO parameters
 * @param {Object} queryParams - Query parameters
 * @returns {string} - Complete SEO URL
 */
export const buildSeoUrl = (type, seoParams, queryParams = {}) => {
  let url = `/${type}/`;
  
  if (type === 'brands') {
    // Brands URL: /brands/{brand}?
    if (seoParams.brands && seoParams.brands.length > 0) {
      url += seoParams.brands[0];
    }
  } else {
    // Listings/Products URL: /{type}/{weapon_category}-ammo/{caliber}/{brand}?
    if (seoParams.weapon_category) {
      url += seoParams.weapon_category;
    }
    
    if (seoParams.caliber) {
      url += `/${seoParams.caliber}`;
    }
    
    if (seoParams.brands && seoParams.brands.length > 0) {
      url += `/${seoParams.brands[0]}`;
    }
  }
  
  // Add query parameters
  const queryString = Object.keys(queryParams)
    .filter(key => queryParams[key] !== undefined && queryParams[key] !== '')
    .map(key => `${key}=${encodeURIComponent(queryParams[key])}`)
    .join('&');
  
  if (queryString) {
    url += `?${queryString}`;
  }
  
  return url;
};

/**
 * Parses SEO URL to extract parameters
 * @param {string} pathname - URL pathname
 * @param {string} search - URL search params
 * @returns {Object} - Parsed parameters
 */
export const parseSeoUrl = (pathname, search = '') => {
  const segments = pathname.split('/').filter(Boolean);
  const params = {
    type: segments[0], // 'ls', 'pd', or 'brands'
    weapon_category: null,
    caliber: null,
    brand: null
  };
  
  if (segments[0] === 'brands') {
    // Brands URL: /brands/{brand}
    params.brand = segments[1];
  } else if (segments.length >= 2) {
    // Listings/Products URL: /{type}/{weapon_category}-ammo/{caliber}/{brand}
    params.weapon_category = segments[1];
    
    if (segments[2]) {
      params.caliber = segments[2];
    }
    
    if (segments[3]) {
      params.brand = segments[3];
    }
  }
  
  // Parse query parameters
  const queryParams = {};
  if (search) {
    const urlParams = new URLSearchParams(search);
    for (const [key, value] of urlParams) {
      queryParams[key] = value;
    }
  }
  
  return {
    ...params,
    queryParams
  };
};

/**
 * Validates SEO slug format
 * @param {string} slug - SEO slug to validate
 * @param {string} type - Type of slug (weapon_category, caliber, brand, etc.)
 * @returns {Object} - Validation result
 */
export const validateSeoSlug = (slug, type = 'general') => {
  if (!slug || typeof slug !== 'string') {
    return { valid: false, error: 'Slug must be a non-empty string' };
  }
  
  // Basic validation patterns
  const patterns = {
    weapon_category: /^(handgun|rifle|rimfire|shotgun)-ammo$/,
    caliber: /^[a-z0-9-]+$/,
    brand: /^[a-zA-Z0-9\s-]+$/,
    bullet_type: /^[a-z0-9-]+$/,
    material: /^[a-z0-9-]+$/,
    general: /^[a-zA-Z0-9\s-]+$/
  };
  
  const pattern = patterns[type] || patterns.general;
  
  if (!pattern.test(slug)) {
    return { 
      valid: false, 
      error: `Invalid ${type} slug format. Must match pattern: ${pattern}` 
    };
  }
  
  // Length validation
  if (slug.length < 2) {
    return { valid: false, error: 'Slug must be at least 2 characters long' };
  }
  
  if (slug.length > 100) {
    return { valid: false, error: 'Slug must be less than 100 characters' };
  }
  
  // Check for consecutive hyphens or spaces
  if (slug.includes('--') || slug.includes('  ')) {
    return { valid: false, error: 'Slug cannot contain consecutive hyphens or spaces' };
  }
  
  // Check for leading/trailing hyphens or spaces
  if (slug.startsWith('-') || slug.endsWith('-') || 
      slug.startsWith(' ') || slug.endsWith(' ')) {
    return { valid: false, error: 'Slug cannot start or end with hyphens or spaces' };
  }
  
  return { valid: true };
};

/**
 * Validates multiple SEO slugs
 * @param {Array} slugs - Array of slugs to validate
 * @param {string} type - Type of slugs
 * @returns {Object} - Validation result with details
 */
export const validateSeoSlugs = (slugs, type = 'general') => {
  if (!Array.isArray(slugs)) {
    return { valid: false, error: 'Slugs must be an array' };
  }
  
  const results = slugs.map(slug => validateSeoSlug(slug, type));
  const invalidSlugs = results.filter(result => !result.valid);
  
  return {
    valid: invalidSlugs.length === 0,
    results,
    invalidCount: invalidSlugs.length,
    errors: invalidSlugs.map(result => result.error)
  };
};

/**
 * Sanitizes a string to create a valid SEO slug
 * @param {string} input - Input string to sanitize
 * @returns {string} - Sanitized SEO slug
 */
export const sanitizeSeoSlug = (input) => {
  if (!input || typeof input !== 'string') {
    return '';
  }
  
  return input
    .toLowerCase()
    .trim()
    // Replace spaces and special characters with hyphens
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    // Remove leading/trailing hyphens
    .replace(/^-+|-+$/g, '');
};

/**
 * Validates complete SEO URL structure
 * @param {Object} seoParams - SEO parameters object
 * @param {Object} facetMappings - Facet mappings for validation
 * @returns {Object} - Validation result
 */
export const validateSeoUrlParams = (seoParams, facetMappings) => {
  const errors = [];
  const warnings = [];
  
  // Validate weapon_category
  if (seoParams.weapon_category) {
    const validation = validateSeoSlug(seoParams.weapon_category, 'weapon_category');
    if (!validation.valid) {
      errors.push(`weapon_category: ${validation.error}`);
    }
  }
  
  // Validate caliber
  if (seoParams.caliber) {
    const validation = validateSeoSlug(seoParams.caliber, 'caliber');
    if (!validation.valid) {
      errors.push(`caliber: ${validation.error}`);
    }
  }
  
  // Validate brand
  if (seoParams.brand) {
    const validation = validateSeoSlug(seoParams.brand, 'brand');
    if (!validation.valid) {
      errors.push(`brand: ${validation.error}`);
    }
  }
  
  // Validate brands array
  if (seoParams.brands && Array.isArray(seoParams.brands)) {
    const validation = validateSeoSlugs(seoParams.brands, 'brand');
    if (!validation.valid) {
      errors.push(`brands: ${validation.errors.join(', ')}`);
    }
  }
  
  // Validate other arrays
  const arrayFields = ['bullet_types', 'materials', 'rounds_per_box', 'shell_lengths'];
  arrayFields.forEach(field => {
    if (seoParams[field] && Array.isArray(seoParams[field])) {
      const validation = validateSeoSlugs(seoParams[field], 'general');
      if (!validation.valid) {
        errors.push(`${field}: ${validation.errors.join(', ')}`);
      }
    }
  });
  
  // Check if slugs exist in mappings (if mappings provided)
  if (facetMappings) {
    Object.entries(seoParams).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach(slug => {
          const facetType = getFacetTypeFromSeoKey(key);
          if (facetType && facetMappings[`${facetType}_reverse`] && 
              !facetMappings[`${facetType}_reverse`][slug]) {
            warnings.push(`Unknown ${facetType} slug: ${slug}`);
          }
        });
      } else if (typeof value === 'string') {
        const facetType = key;
        if (facetMappings[`${facetType}_reverse`] && 
            !facetMappings[`${facetType}_reverse`][value]) {
          warnings.push(`Unknown ${facetType} slug: ${value}`);
        }
      }
    });
  }
  
  return {
    valid: errors.length === 0,
    errors,
    warnings,
    hasWarnings: warnings.length > 0
  };
};

